import { FlattenedUser } from '../../types/user';
import { useSelector } from 'react-redux';
import { useToast } from '../../hooks/useToast';
import {
	useCallback,
	useContext,
	useEffect,
	useState,
	useMemo,
	useRef,
} from 'react';
import {
	InvoiceStatus,
	Exact,
	InvoiceEdit_RequestInvoiceAttachmentUploadUrlQuery,
	InvoiceEditInvoiceFragment,
	InvoiceWhereUniqueInput,
	RequestAttachmentUploadUrlInput,
	UserType,
	UseLineItemsInvoiceFragment,
	InvoiceCreateInput,
	InvoiceUpdateInput,
	InvoiceEdit_CreateDraftInvoiceMutation,
	InvoiceEdit_UpdateInvoiceMutation,
	UsePaymentInstallmentsInvoiceEditInvoiceFragment,
	InstallmentStatus,
} from '#types';
import { ContactsContext } from '../../context/ContactsContextProvider/ContactsContextProvider';
import { CreateOrUpdateInvoiceHandler, InvoiceTemplate } from './types';
import { useLineItems, getNewItemId as getNewLineItemId } from './useLineItems';
import Globals from '../../lib/Globals';
import API from '../../lib/API';
import {
	usePaymentInstallments,
	getNewItemId as getNewInstallmentId,
} from './usePaymentInstallments';
import rpcShared from '@rockpapercoin/rpc-shared';
import dateIsValid from '../../lib/helpers/dateIsValid';
import { createBundleObject } from '../../lib/helpers';
import { filterTypenameFromArray } from '../../lib/helpers/apollo';
import { useInvoiceTemplateFromUrlParamIfProvided } from './useInvoiceTemplateFromUrlParamIfProvided';
import { notificationGroupGraphqlErrorFiltering } from '../NotificationGroup/notificationGroupGraphqlErrorFiltering';
import {
	ApolloCache,
	DefaultContext,
	FetchResult,
	LazyQueryExecFunction,
	MutationFunctionOptions,
} from '@apollo/client';
import { UseNotificationGroupHookResults } from '../NotificationGroup/useNotificationGroup';
import { useUpdateStateFromBundleOrContact } from '../../hooks/useUpdateStateFromBundleOrContact';
import { INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID } from '../../lib/editorBlockerIds';
import { NavigationBlockingContext } from '../../context/NavigationBlockingContextProvider';
import { Differences } from '../../lib/helpers/deepCompareAndDiff';

type UseInvoiceEditInvoice = Omit<InvoiceEditInvoiceFragment, 'id'> & {
	id?: string;
};

type UseInvoiceEditProps = {
	invoiceId?: string;
	invoice?: UseInvoiceEditInvoice;
	setInvoice: React.Dispatch<
		React.SetStateAction<UseInvoiceEditInvoice | undefined>
	>;
	contactId?: string;
	requestInvoiceAttachmentUploadUrl: LazyQueryExecFunction<
		InvoiceEdit_RequestInvoiceAttachmentUploadUrlQuery,
		Exact<{
			data: RequestAttachmentUploadUrlInput;
			where: InvoiceWhereUniqueInput;
		}>
	>;
	createDraftInvoice: (
		options?:
			| MutationFunctionOptions<
					InvoiceEdit_CreateDraftInvoiceMutation,
					Exact<{
						data: InvoiceCreateInput;
					}>,
					DefaultContext,
					ApolloCache<any>
			  >
			| undefined
	) => Promise<FetchResult<InvoiceEdit_CreateDraftInvoiceMutation>>;
	updateInvoice: (
		options?:
			| MutationFunctionOptions<
					InvoiceEdit_UpdateInvoiceMutation,
					Exact<{
						data: InvoiceUpdateInput;
						where: InvoiceWhereUniqueInput;
					}>,
					DefaultContext,
					ApolloCache<any>
			  >
			| undefined
	) => Promise<FetchResult<InvoiceEdit_UpdateInvoiceMutation>>;
	fileOrFilename?: File | string | undefined;
	setFileOrFilename: React.Dispatch<
		React.SetStateAction<File | string | undefined>
	>;
	notificationGroupProps: UseNotificationGroupHookResults;
	skipInstallmentCalculation?: boolean;
	setSkipInstallmentCalculation: React.Dispatch<React.SetStateAction<boolean>>;
	invoiceEdits?: Differences;
};

export const useInvoiceEdit = ( {
	invoiceId,
	invoice,
	setInvoice,
	contactId,
	requestInvoiceAttachmentUploadUrl,
	createDraftInvoice,
	updateInvoice,
	fileOrFilename,
	setFileOrFilename,
	notificationGroupProps,
	skipInstallmentCalculation = false,
	setSkipInstallmentCalculation,
	invoiceEdits,
}: UseInvoiceEditProps ) => {
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser ? user : undefined;
	const { showError } = useToast();

	const contactsContext = useContext( ContactsContext );

	const [ eventDateValidation, setEventDateValidation ] = useState<
		string | undefined
	>();
	const [ titleValidation, setTitleValidation ] = useState<string | undefined>();
	const [ paymentInstallmentsSelectValidation, setPaymentInstallmentsSelectValidation, ] = useState<string | undefined>();
	const [ fileValidation, setFileValidation ] = useState<string | undefined>();
	const [ isUploading, setIsUploading ] = useState( false );
	const [ intentToChangeOrDeleteCurrentAttachmentFile, setIntentToChangeOrDeleteCurrentAttachmentFile, ] = useState( false );

	const [ invoiceTemplate, setInvoiceTemplate ] = useState<
		InvoiceTemplate | null | undefined
	>();

	// invoiceTemplate should reflect the API value when said value changes, otherwise it's also local state
	useEffect( () => {
		setInvoiceTemplate( ( prevState ) =>
			( !prevState && invoice?.invoiceTemplate?.id ) ||
			( prevState && prevState.id !== invoice?.invoiceTemplate?.id )
				? invoice?.invoiceTemplate
				: prevState
		);
	}, [ invoice?.invoiceTemplate ] );

	const backfillRequiredInvoiceFields = useCallback(
		(
			prevState:
				| ( Omit<InvoiceEditInvoiceFragment, 'id'> & { id?: string } )
				| undefined
		) => ( {
			...prevState,
			...( typeof prevState?.title === 'string'
				? { title: prevState.title }
				: { title: '' } ),
			...( typeof prevState?.allowsGratuity === 'boolean'
				? { allowsGratuity: prevState.allowsGratuity }
				: { allowsGratuity: true } ),
			...( typeof prevState?.automatedRemindersEnabled === 'boolean'
				? { automatedRemindersEnabled: prevState.automatedRemindersEnabled }
				: { automatedRemindersEnabled: true } ),
			...( typeof prevState?.totalAmount === 'number'
				? { totalAmount: prevState.totalAmount }
				: { totalAmount: 0 } ),
			...( typeof prevState?.status === 'string'
				? { status: prevState.status }
				: { status: InvoiceStatus.Draft } ),
			...( prevState?.lineItems
				? { lineItems: prevState.lineItems }
				: { lineItems: [] } ),
			...( prevState?.paymentInstallments
				? { paymentInstallments: prevState.paymentInstallments }
				: { paymentInstallments: [] } ),
		} ),
		[]
	);

	const bundle = useMemo(
		() => ( contactId ? createBundleObject( contactId ) : undefined ),
		[ contactId ]
	);

	// Bundle actions for setting state from the bundle cookie
	useUpdateStateFromBundleOrContact( {
		isNewDocument: !invoiceId && !invoice?.id,
		contactId,
		set: setInvoice as Parameters<
			typeof useUpdateStateFromBundleOrContact<true>
		>[0]['set'],
		isInvoice: true,
	} );

	const {
		validate: validateLineItems,
		lineItemsDetails,
		onDragEnd,
		onAddLineItem,
		onRemoveLineItem,
		onUpdateLineItem,
		lineItemsInput,
		onBlur: onBlurLineItem,
		replaceLineItems,
	} = useLineItems( {
		invoiceId,
		invoice,
		setInvoice: setInvoice as React.Dispatch<
			React.SetStateAction<UseLineItemsInvoiceFragment>
		>,
	} );

	const {
		validate: validatePaymentInstallments,
		paymentInstallmentsDetails,
		onAddPaymentInstallments,
		onRemovePaymentInstallment,
		onUpdatePaymentInstallment,
		paymentInstallmentsInput,
		onBlur: onBlurPaymentInstallment,
		replacePaymentInstallments,
	} = usePaymentInstallments( {
		invoice,
		setInvoice: setInvoice as React.Dispatch<
			React.SetStateAction<UsePaymentInstallmentsInvoiceEditInvoiceFragment>
		>,
		skipInstallmentCalculation,
	} );

	// notification group state
	const {
		onValidate: validateNotificationGroup,
		...notificationGroupPropsRest
	} = notificationGroupProps;

	const contactsContextExists = !!contactsContext;
	const organization = orgUser?.organization;
	const firstContactInContext = contactsContext?.contacts?.[ 0 ];
	useEffect( () => {
		// if we have an invoice, lets set the contact in state
		if (
			invoiceId &&
			invoice &&
			invoice.contact &&
			contactsContextExists &&
			!firstContactInContext
		) {
			contactsContext.set( invoice.contact );
		}
	}, [
		contactsContext,
		contactsContextExists,
		firstContactInContext,
		invoice,
		invoiceId,
	] );

	// set default invoice settings values from the organization as a default
	useEffect( () => {
		if ( !invoiceId && organization ) {
			setInvoice( ( prevState ) =>
				typeof prevState?.allowsGratuity !== 'boolean' &&
				typeof prevState?.automatedRemindersEnabled !== 'boolean'
					? {
						...prevState,
						...backfillRequiredInvoiceFields( prevState ),
						allowsGratuity: organization.invoicesAllowGratuity,
						automatedRemindersEnabled: organization.automatedRemindersEnabled,
					  }
					: prevState
			);
		}
	}, [
		backfillRequiredInvoiceFields,
		invoiceId,
		organization,
		setInvoice
	] );

	// non-rendering state indicating if the user has clicked "Prepare" yet to avoid delays/more complexity here. "Spending" duplicate state to save on branching code. */
	const validationEnabledRef = useRef( false );

	const [ buttonsAreDisabled, setButtonsAreDisabled ] = useState( true );

	// Looks like we're waiting until the contacts hook is ready to set the buttons to be enabled
	useEffect( () => {
		if ( contactsContext?.organizationContactsReady ) {
			setButtonsAreDisabled( false );
		}
	}, [ contactsContext?.organizationContactsReady ] );

	const [ contactOptional, setContactOptional ] = useState( false );
	useEffect( () => {
		if ( invoice?.status && invoice.status !== InvoiceStatus.Draft ) {
			setContactOptional( false );
		}
	}, [ invoice?.status ] );

	/* Simple validation handler */
	const validate = useCallback(
		( intendToSend: boolean ) => {
			const contactIsValid = !!contactsContext?.validate( intendToSend );
			if ( validationEnabledRef.current ) {
				// if we're not intending to send then we don't have to validate everything
				let invoiceIsValid = true;
				if ( !invoice?.title?.trim() ) {
					setTitleValidation( 'Enter in a name for the invoice.' );
					invoiceIsValid = false;
				} else {
					setTitleValidation( undefined );
				}
				if ( !intendToSend ) {
					return invoiceIsValid;
				}
				// otherwise, validate into separate variables here, as invoiceIsValid = thing1() & thing2() does not execute thing2() idf thing1() fails
				const lineItemsValid = validateLineItems( intendToSend );
				const notSendingOrPaymentInstallmentsArrayNotEmpty =
					!intendToSend ||
					!!( invoice && invoice?.paymentInstallments?.length > 0 );
				const paymentInstallmentsValid =
					validatePaymentInstallments( intendToSend ) &&
					notSendingOrPaymentInstallmentsArrayNotEmpty;
				invoiceIsValid = lineItemsValid && paymentInstallmentsValid;

				if ( !notSendingOrPaymentInstallmentsArrayNotEmpty ) {
					setPaymentInstallmentsSelectValidation(
						rpcShared.strings.errorMessages.invoicesRequireInstallmentsToSend
					);
					invoiceIsValid = false;
				} else {
					setPaymentInstallmentsSelectValidation( undefined );
				}

				if (
					fileOrFilename instanceof File &&
					fileOrFilename.size > Globals.maxAttachmentSize
				) {
					invoiceIsValid = false;
					setFileValidation( 'Files must be smaller than 10MB.' );
				} else {
					setFileValidation( undefined );
				}
				if ( invoice?.eventDate && !dateIsValid( invoice.eventDate ) ) {
					setEventDateValidation( 'Invalid date.' );
					invoiceIsValid = false;
				} else {
					setEventDateValidation( undefined );
				}

				const notificationGroupIsValid = validateNotificationGroup();
				return invoiceIsValid && contactIsValid && notificationGroupIsValid;
			}
			return contactIsValid;
		},
		[
			contactsContext,
			fileOrFilename,
			invoice,
			validateLineItems,
			validateNotificationGroup,
			validatePaymentInstallments,
		]
	);

	const createOrUpdateInvoiceHandler: CreateOrUpdateInvoiceHandler =
		useCallback(
			async ( options ) => {
				/* this ref indicates if the user has taken action and therefore we should start showing validation */
				validationEnabledRef.current = true;
				const contacts = await contactsContext?.commit();
				const validateAsIntendToSend =
					options?.intendToSend ||
					( invoice?.status && invoice.status !== InvoiceStatus.Draft ) ||
					false;
				const contactsAreValid =
					!validateAsIntendToSend ||
					( validateAsIntendToSend && contacts && contacts.length > 0 );
				if (
					// first, the actual checks that affect state
					!validate( validateAsIntendToSend ) ||
					// then, if we intend to send or the invoice is not a draft, exit if we get an error with out contact, not just no-contact
					!contactsAreValid ||
					// followed by the multiple typescript assurances that we cannot communicate from the one function response
					!invoice?.title
				) {
					return false;
				}

				const uploadFileAttachment = async ( file: File, invoiceId: string ) => {
					setIsUploading( true );
					const uploadUrlResponse = await requestInvoiceAttachmentUploadUrl( {
						variables: {
							where: { id: invoiceId },
							data: { filename: file.name, fileType: file.type },
						},
					} );
					if ( uploadUrlResponse.error ) {
						setIsUploading( false );
						showError( uploadUrlResponse.error );
						return false;
					} else if (
						uploadUrlResponse.data?.requestInvoiceAttachmentUploadUrl.uploadURL
					) {
						const uploadUrl =
							uploadUrlResponse.data.requestInvoiceAttachmentUploadUrl
								.uploadURL;
						const uploadResponse = await API.putFile( file, uploadUrl );
						setIsUploading( false );
						await updateInvoice( {
							variables: {
								where: { id: invoiceId },
								data: {
									title: invoice.title,
									uploadAttachment:
										rpcShared.strings.escapeInvalidCharactersFromFilename(
											file.name
										),
									lineItems: lineItemsInput,
									notificationGroupMembers:
										notificationGroupPropsRest.value.map(
											( option ) => option.value
										),
								},
							},
						} );
						if ( 'imageUrl' in uploadResponse && !uploadResponse.imageUrl ) {
							showError( 'Error uploading file.' );
							return false;
						}
					}
				};

				const contact =
					contacts && contacts.length ? { id: contacts[ 0 ].id } : null;

				const scalarInvoiceData:
					| InvoiceCreateInput
					| ( Omit<InvoiceUpdateInput, 'title'> & { title: string } ) = {
						allowsGratuity: invoice.allowsGratuity,
						automatedRemindersEnabled: invoice.automatedRemindersEnabled,
						eventDate: invoice.eventDate,
						title: invoice.title,
						discountAmount: invoice.discountAmount,
						discountPercentage: invoice.discountPercentage,
						discountIsPercentage: invoice.discountIsPercentage,
						notes: invoice.notes,
					};

				// If we're on /invoice/create then make the new draft
				if ( !invoiceId ) {
					setSkipInstallmentCalculation( true );
					const createDraftResponse = await createDraftInvoice( {
						variables: {
							data: {
								...scalarInvoiceData,
								contact,
								lineItems: lineItemsInput,
								paymentInstallments: paymentInstallmentsInput,
								...( invoiceTemplate
									? {
										invoiceTemplate: {
											connect: { id: invoiceTemplate.id },
										},
									  }
									: {} ),
								notificationGroupMembers: notificationGroupPropsRest.value.map(
									( option ) => option.value
								),
							},
						},
					} );
					if ( createDraftResponse.errors ) {
						setSkipInstallmentCalculation( false );
						const { validation, remainingErrors } =
							notificationGroupGraphqlErrorFiltering(
								createDraftResponse.errors,
								true
							);
						notificationGroupPropsRest.setValidation( validation?.InvoiceCopy );
						remainingErrors?.forEach( ( error ) => showError( error ) );
						return false;
					} else if ( createDraftResponse.data ) {
						if ( fileOrFilename instanceof File ) {
							await uploadFileAttachment(
								fileOrFilename,
								createDraftResponse.data.createDraftInvoice.id
							);
						}
						return createDraftResponse.data.createDraftInvoice;
					} else {
						// we shouldn't be able to get here
						return false;
					}
				} else {
					setSkipInstallmentCalculation( true );
					if ( fileOrFilename instanceof File ) {
						const res = await uploadFileAttachment( fileOrFilename, invoiceId );
						if ( res === false ) {
							return false;
						}
					}
					const updateResponse = await updateInvoice( {
						variables: {
							where: { id: invoiceId },
							data: {
								...scalarInvoiceData,
								contact,
								lineItems: lineItemsInput,
								paymentInstallments: paymentInstallmentsInput,
								...( intentToChangeOrDeleteCurrentAttachmentFile &&
								!fileOrFilename &&
								invoice?.uploadAttachment
									? { uploadAttachment: null }
									: {} ),
								...( invoiceTemplate
									? {
										invoiceTemplate: {
											connect: { id: invoiceTemplate.id },
										},
									  }
									: {} ),
								notificationGroupMembers: notificationGroupPropsRest.value.map(
									( option ) => option.value
								),
							},
						},
					} );
					if ( updateResponse.errors ) {
						setSkipInstallmentCalculation( false );
						const { validation, remainingErrors } =
							notificationGroupGraphqlErrorFiltering(
								updateResponse.errors,
								true
							);
						notificationGroupPropsRest.setValidation( validation?.InvoiceCopy );
						remainingErrors?.forEach( ( error ) => showError( error ) );
						return false;
					} else if ( updateResponse.data?.updateInvoice ) {
						// now that we've done what we wanted to do, reset the state related to potential attachment changes
						setIntentToChangeOrDeleteCurrentAttachmentFile( ( prevState ) =>
							intentToChangeOrDeleteCurrentAttachmentFile &&
							!fileOrFilename &&
							invoice?.uploadAttachment
								? false
								: prevState
						);
						return updateResponse.data.updateInvoice;
					} else {
						showError( rpcShared.strings.errorMessages.default );
						return false;
					}
				}
			},
			[
				contactsContext,
				validate,
				invoice,
				invoiceId,
				requestInvoiceAttachmentUploadUrl,
				showError,
				updateInvoice,
				lineItemsInput,
				notificationGroupPropsRest,
				createDraftInvoice,
				paymentInstallmentsInput,
				invoiceTemplate,
				fileOrFilename,
				intentToChangeOrDeleteCurrentAttachmentFile,
				setSkipInstallmentCalculation,
			]
		);

	const handleSetInvoiceTemplate = useCallback(
		( invoiceTemplate?: InvoiceTemplate | null ) => {
			setInvoiceTemplate( invoiceTemplate );
			if ( invoiceTemplate ) {
				const {
					notes,
					allowsGratuity,
					automatedRemindersEnabled,
					discountAmount,
					discountIsPercentage,
					discountPercentage,
					filename,
					lineItems,
					paymentInstallments,
				} = invoiceTemplate;
				setInvoice( ( prevState ) => ( {
					...prevState,
					...backfillRequiredInvoiceFields( prevState ),
					discountAmount,
					discountIsPercentage,
					discountPercentage,
					notes,
					allowsGratuity,
					automatedRemindersEnabled,
					lineItems: filterTypenameFromArray( lineItems )
						.sort( ( a, b ) => a.order - b.order )
						.map( ( lineItem, index, array ) => ( {
							...lineItem,
							id: getNewLineItemId( index, array.length ),
						} ) ),
					paymentInstallments: filterTypenameFromArray( paymentInstallments ).map(
						( paymentInstallment, index, array ) => ( {
							...paymentInstallment,
							id: getNewInstallmentId( index, array.length ),
							status: InstallmentStatus.Unpaid,
							dueDate: null,
						} )
					),
				} ) );
				setFileOrFilename( filename || undefined );
				if ( !filename ) {
					setIntentToChangeOrDeleteCurrentAttachmentFile( true );
				}
				/* This "replace" method is used with applying invoice templates only, and only alters
				details. The actual invoice line items are replaced above. */
				replaceLineItems(
					filterTypenameFromArray(
						[ ...lineItems ]
							.sort( ( a, b ) => a.order - b.order )
							.map( ( lineItem, index ) => ( {
								...lineItem,
								id: getNewLineItemId( index ),
							} ) )
					)
				);
				/* This "replace" method is used with applying invoice templates only, and only alters
				details. The actual invoice payment installments are replaced above. */
				replacePaymentInstallments(
					filterTypenameFromArray( paymentInstallments ).map(
						( paymentInstallment, index ) => ( {
							...paymentInstallment,
							id: getNewInstallmentId( index ),
							status: InstallmentStatus.Unpaid,
							dueDate: null,
						} )
					)
				);
			}
		},
		[
			backfillRequiredInvoiceFields,
			replaceLineItems,
			replacePaymentInstallments,
			setFileOrFilename,
			setInvoice,
		]
	);

	const { loading: invoiceTemplateFromUrlLoading } =
		useInvoiceTemplateFromUrlParamIfProvided( handleSetInvoiceTemplate );

	const [ unsavedChangesModalOpen, setUnsavedChangesModalOpen ] = useState( false );
	const [ isSaving, setIsSaving ] = useState( false );
	const resolveNavigationRef = useRef<( ( allowed: boolean ) => void ) | null>(
		null
	);
	const { addBlocker, removeBlocker } =
		useContext( NavigationBlockingContext ) ?? {};

	useEffect( () => {
		if ( !invoiceEdits || !addBlocker ) {
			removeBlocker?.( INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID );
			return;
		}

		addBlocker(
			INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID,
			( _event ) =>
				new Promise<boolean>( ( resolve ) => {
					resolveNavigationRef.current = resolve;
					setUnsavedChangesModalOpen( true );
				} )
		);

		return () => {
			removeBlocker?.( INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID );
		};
	}, [
		invoiceEdits,
		addBlocker,
		removeBlocker
	] );

	const handleModalSave = useCallback( async () => {
		setIsSaving( true );
		/* this `setContactOptional` usage is also in
		`src/blocks/InvoiceEdit/InvoiceEditButtonBar/useInvoiceEditButtonBarSaveButton.tsx` and
		probably should be moved into `createOrUpdateInvoiceHandler` when `intendToSend` is
		`false` */
		if ( !invoice?.status || invoice.status === InvoiceStatus.Draft ) {
			setContactOptional( ( prevState ) => ( prevState ? prevState : true ) );
			await new Promise( ( resolve ) => setTimeout( resolve, 0 ) ); // wait a beat for a render cycle for state to update
		}
		const result = await createOrUpdateInvoiceHandler( { intendToSend: false } );
		setIsSaving( false );
		setUnsavedChangesModalOpen( false );
		resolveNavigationRef.current?.( !!result );
		resolveNavigationRef.current = null;
	}, [ createOrUpdateInvoiceHandler, invoice?.status ] );

	const handleModalNavigate = useCallback( () => {
		setUnsavedChangesModalOpen( false );
		removeBlocker?.( INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID );
		resolveNavigationRef.current?.( true );
		resolveNavigationRef.current = null;
	}, [ removeBlocker ] );

	const handleModalClose = useCallback( () => {
		setUnsavedChangesModalOpen( false );
		resolveNavigationRef.current?.( false );
		resolveNavigationRef.current = null;
	}, [] );

	return {
		eventDateValidation,
		titleValidation,
		paymentInstallmentsSelectValidation,
		fileValidation,
		notificationGroupProps,
		invoiceTemplate,
		handleSetInvoiceTemplate,
		createOrUpdateInvoiceHandler,
		onDragEnd,
		onAddLineItem,
		onRemoveLineItem,
		onUpdateLineItem,
		onBlurLineItem,
		lineItemsDetails,
		buttonsAreDisabled: buttonsAreDisabled || invoiceTemplateFromUrlLoading,
		paymentInstallmentsDetails,
		onAddPaymentInstallments,
		onRemovePaymentInstallment,
		onUpdatePaymentInstallment,
		onBlurPaymentInstallment,
		isUploading,
		validationEnabledRef,
		validate,
		bundle,
		intentToChangeOrDeleteCurrentAttachmentFile,
		setIntentToChangeOrDeleteCurrentAttachmentFile,
		contactOptional,
		setContactOptional,
		unsavedChangesModalOpen,
		isSaving,
		handleModalSave,
		handleModalNavigate,
		handleModalClose,
	};
};
